const HISTORY_KEY = 'ovalia.search.history.v1';
const HISTORY_LIMIT = 5;
export const normalizeSearch = (value) => [...String(value ?? '').replace(/\s+/gu, ' ').trim()].slice(0, 80).join('');

export function readSearchHistory(storage) {
    try {
        const values = JSON.parse(storage?.getItem(HISTORY_KEY) || '[]');
        if (!Array.isArray(values)) return [];
        return [...new Set(values.filter((value) => typeof value === 'string').map(normalizeSearch))]
            .filter((value) => [...value].length >= 2).slice(0, HISTORY_LIMIT);
    } catch { return []; }
}

export function rememberSearch(storage, value) {
    const query = normalizeSearch(value);
    if ([...query].length < 2) return;
    const history = readSearchHistory(storage).filter((item) => item.toLocaleLowerCase('pl') !== query.toLocaleLowerCase('pl'));
    try { storage?.setItem(HISTORY_KEY, JSON.stringify([query, ...history].slice(0, HISTORY_LIMIT))); } catch { /* Private browsing or storage quota: search still works. */ }
}

export function clearSearchHistory(storage) {
    try { storage?.removeItem(HISTORY_KEY); } catch { /* Storage is optional. */ }
}

/** A single latest-request pipeline. Aborted or out-of-order responses never reach the view. */
export class ProductSearchRequest {
    constructor({ url, action, minLength = 2, onState, delay = 280, timeout = 10000, fetcher = (...args) => globalThis.fetch(...args), timers = globalThis }) {
        Object.assign(this, { url, action, minLength, onState, delay, timeout, fetcher, timers });
        this.revision = 0;
    }

    cancel() {
        this.revision += 1;
        this.timers.clearTimeout(this.pending);
        this.timers.clearTimeout(this.deadline);
        this.controller?.abort();
    }

    search(value, immediate = false) {
        this.cancel();
        const query = normalizeSearch(value);
        if ([...query].length < this.minLength) {
            this.onState({ state: 'initial', query });
            return;
        }
        const revision = this.revision;
        this.onState({ state: 'loading', query });
        this.pending = this.timers.setTimeout(() => this.run(query, revision), immediate ? 0 : this.delay);
    }

    async run(query, revision) {
        if (revision !== this.revision) return;
        const controller = new AbortController();
        this.controller = controller;
        this.deadline = this.timers.setTimeout(() => {
            if (revision !== this.revision) return;
            this.cancel();
            this.onState({ state: 'error', query });
        }, this.timeout);
        try {
            const url = new URL(this.url);
            url.searchParams.set('action', this.action);
            url.searchParams.set('s', query);
            const response = await this.fetcher(url.toString(), { signal: controller.signal, credentials: 'same-origin', headers: { Accept: 'application/json' } });
            if (!response.ok) throw new Error('Search request failed');
            const payload = await response.json();
            if (!payload.success || typeof payload.data?.html !== 'string') throw new Error('Invalid search response');
            if (revision === this.revision) this.onState({ state: 'success', query, data: payload.data });
        } catch {
            if (revision === this.revision) this.onState({ state: 'error', query });
        } finally {
            if (revision === this.revision) this.timers.clearTimeout(this.deadline);
        }
    }
}
